// Timezone option list and IANA <-> slug helpers.
//
// The app persists the user's timezone as a lowercase, hyphenated slug
// (e.g. "europe-lisbon"), while the browser's Intl API and most date utilities
// use IANA identifiers (e.g. "Europe/Lisbon"). These helpers convert between
// the two formats and centralise the canonical option list so the Settings
// dropdown, onboarding auto-detection, and date formatting all agree.

export interface TimezoneOption {
  value: string; // slug, e.g. "europe-lisbon"
  label: string; // e.g. "🇵🇹 Lisboa (WET, UTC+0)"
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: "america-los-angeles", label: "🇺🇸 Los Angeles (PST, UTC-8)" },
  { value: "america-denver", label: "🇺🇸 Denver (MST, UTC-7)" },
  { value: "america-chicago", label: "🇺🇸 Chicago (CST, UTC-6)" },
  { value: "america-new-york", label: "🇺🇸 New York (EST, UTC-5)" },
  { value: "america-sao-paulo", label: "🇧🇷 São Paulo (BRT, UTC-3)" },
  { value: "america-buenos-aires", label: "🇦🇷 Buenos Aires (ART, UTC-3)" },
  { value: "europe-lisbon", label: "🇵🇹 Lisboa (WET, UTC+0)" },
  { value: "europe-london", label: "🇬🇧 London (GMT, UTC+0)" },
  { value: "europe-dublin", label: "🇮🇪 Dublin (GMT, UTC+0)" },
  { value: "europe-madrid", label: "🇪🇸 Madrid (CET, UTC+1)" },
  { value: "europe-paris", label: "🇫🇷 Paris (CET, UTC+1)" },
  { value: "europe-amsterdam", label: "🇳🇱 Amsterdam (CET, UTC+1)" },
  { value: "europe-brussels", label: "🇧🇪 Brussels (CET, UTC+1)" },
  { value: "europe-berlin", label: "🇩🇪 Berlin (CET, UTC+1)" },
  { value: "europe-rome", label: "🇮🇹 Rome (CET, UTC+1)" },
  { value: "europe-vienna", label: "🇦🇹 Vienna (CET, UTC+1)" },
  { value: "europe-stockholm", label: "🇸🇪 Stockholm (CET, UTC+1)" },
  { value: "europe-copenhagen", label: "🇩🇰 Copenhagen (CET, UTC+1)" },
  { value: "europe-warsaw", label: "🇵🇱 Warsaw (CET, UTC+1)" },
  { value: "europe-prague", label: "🇨🇿 Prague (CET, UTC+1)" },
  { value: "europe-bratislava", label: "🇸🇰 Bratislava (CET, UTC+1)" },
  { value: "europe-budapest", label: "🇭🇺 Budapest (CET, UTC+1)" },
  { value: "europe-ljubljana", label: "🇸🇮 Ljubljana (CET, UTC+1)" },
  { value: "europe-luxembourg", label: "🇱🇺 Luxembourg (CET, UTC+1)" },
  { value: "europe-zagreb", label: "🇭🇷 Zagreb (CET, UTC+1)" },
  { value: "europe-valletta", label: "🇲🇹 Valletta (CET, UTC+1)" },
  { value: "europe-tallinn", label: "🇪🇪 Tallinn (EET, UTC+2)" },
  { value: "europe-helsinki", label: "🇫🇮 Helsinki (EET, UTC+2)" },
  { value: "europe-athens", label: "🇬🇷 Athens (EET, UTC+2)" },
  { value: "europe-bucharest", label: "🇷🇴 Bucharest (EET, UTC+2)" },
  { value: "europe-sofia", label: "🇧🇬 Sofia (EET, UTC+2)" },
  { value: "europe-riga", label: "🇱🇻 Riga (EET, UTC+2)" },
  { value: "europe-vilnius", label: "🇱🇹 Vilnius (EET, UTC+2)" },
  { value: "europe-nicosia", label: "🇨🇾 Nicosia (EET, UTC+2)" },
  { value: "africa-johannesburg", label: "🇿🇦 Johannesburg (SAST, UTC+2)" },
  { value: "africa-cairo", label: "🇪🇬 Cairo (EET, UTC+2)" },
  { value: "africa-nairobi", label: "🇰🇪 Nairobi (EAT, UTC+3)" },
  { value: "asia-dubai", label: "🇦🇪 Dubai (GST, UTC+4)" },
  { value: "asia-kolkata", label: "🇮🇳 Mumbai (IST, UTC+5:30)" },
  { value: "asia-bangkok", label: "🇹🇭 Bangkok (ICT, UTC+7)" },
  { value: "asia-singapore", label: "🇸🇬 Singapore (SGT, UTC+8)" },
  { value: "asia-hong-kong", label: "🇭🇰 Hong Kong (HKT, UTC+8)" },
  { value: "asia-shanghai", label: "🇨🇳 Shanghai (CST, UTC+8)" },
  { value: "asia-tokyo", label: "🇯🇵 Tokyo (JST, UTC+9)" },
  { value: "asia-seoul", label: "🇰🇷 Seoul (KST, UTC+9)" },
  { value: "australia-sydney", label: "🇦🇺 Sydney (AEDT, UTC+11)" },
  { value: "pacific-auckland", label: "🇳🇿 Auckland (NZDT, UTC+13)" },
];

const KNOWN_SLUGS = new Set(TIMEZONE_OPTIONS.map((option) => option.value));

export const isKnownTimezoneSlug = (slug?: string | null): boolean =>
  !!slug && KNOWN_SLUGS.has(slug.trim());

// "Europe/Lisbon" / "America/Los_Angeles" -> "europe-lisbon" / "america-los-angeles".
// A value that is already a slug passes through unchanged.
export const ianaToTimezoneSlug = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.toLowerCase().replace(/[/_]/g, "-");
};

// "europe-lisbon" -> "Europe/Lisbon". Mirrors the conversion used when formatting
// dates so a stored slug can be handed to Intl / Date APIs.
export const timezoneSlugToIana = (slug?: string | null): string | undefined => {
  const trimmed = slug?.trim();
  if (!trimmed) {
    return undefined;
  }
  const parts = trimmed.split("-");
  if (parts.length < 2) {
    return trimmed;
  }
  const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
  const region = capitalize(parts[0]);
  const city = parts
    .slice(1)
    .map((part) => (part.length <= 2 ? part.toUpperCase() : capitalize(part)))
    .join("_");
  return `${region}/${city}`;
};

// Accepts a stored value in either slug or IANA form and returns a normalized slug,
// or undefined when there is no value. Unknown-but-well-formed zones are preserved
// (slugified) so a user's real timezone is never silently discarded.
export const normalizeTimezoneSlug = (value?: string | null): string | undefined =>
  ianaToTimezoneSlug(value);

// Best-effort human label for a slug that isn't in TIMEZONE_OPTIONS, e.g.
// "america-toronto" -> "America/Toronto", so the dropdown can still display it.
export const timezoneSlugToLabel = (slug: string): string =>
  timezoneSlugToIana(slug) ?? slug;

// The browser's current timezone as a slug (e.g. "europe-lisbon"), or undefined
// when the Intl API is unavailable.
export const detectBrowserTimezoneSlug = (): string | undefined => {
  try {
    return ianaToTimezoneSlug(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return undefined;
  }
};
